import { useState } from "react";
import { Link } from "react-router-dom";

import { requestPasswordReset } from "@/lib/auth/auth.api";
import { MarketingSiteLink } from "@/pages/auth/MarketingSiteLink";
import {
  AuthCard,
  AuthPageShell,
  authFieldClass,
  authLinkClass,
  authPrimaryButtonClass,
} from "@/pages/auth/AuthPageShell";

/**
 * Password reset request — always shows success copy (no email enumeration).
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await requestPasswordReset(trimmed);
      setSubmitted(true);
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      const st = ax?.response?.status;
      // D-215 rev 1: a 503 is answered exactly like a 200 (the neutral screen),
      // so no screen differs by whether an account exists. 429 and other
      // failures get messages that say nothing about the account either.
      if (st === 404) {
        setError("Password reset is not available on this server yet. Try again later.");
      } else if (st === 429) {
        setError("Too many attempts. Wait a few minutes and try again.");
      } else if (st === undefined || (st >= 500 && st !== 503)) {
        // No HTTP response (network failure) or a server error other than 503.
        setError("Something went wrong. Please try again.");
      } else {
        setSubmitted(true);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageShell>
      <AuthCard>
        <h1 className="text-lg font-medium text-slate-900">Reset your password</h1>
        <p className="mt-2 text-sm text-slate-500">
          Enter the email you use to sign in. If an account exists, we will send reset instructions.
        </p>

        {error ? (
          <div
            className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
            role="alert"
          >
            {error}
          </div>
        ) : null}

        {submitted ? (
          <div
            className="mt-6 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800"
            role="status"
          >
            If an account exists for that email, we&apos;ve sent a reset link. If it doesn&apos;t arrive in a
            few minutes, contact your administrator.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label htmlFor="forgot-email" className="block text-sm font-medium text-slate-700">
                Email
              </label>
              <input
                id="forgot-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                className={`mt-1 w-full ${authFieldClass}`}
              />
            </div>
            <button type="submit" disabled={submitting} className={authPrimaryButtonClass}>
              {submitting ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-600">
          <Link to="/login" className={authLinkClass}>
            Back to sign in
          </Link>
        </p>
        <p className="mt-3 text-center">
          <MarketingSiteLink />
        </p>
      </AuthCard>
    </AuthPageShell>
  );
}
